import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Save, ClipboardList, Sparkles, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { adminApi, ejerciciosApi, formatApiError, tokenStore } from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPO_LABELS = { estandar: "Estándar", personalizada: "Personalizada" };
const NIVEL_LABELS = { 1: "Iniciación", 2: "Intermedio", 3: "Avanzado" };
const OBJETIVO_LABELS = {
  salud_general: "Salud general",
  ganar_musculo: "Ganar músculo",
  perder_peso: "Perder peso",
  resistencia: "Resistencia",
  rehabilitacion: "Rehabilitación",
};
const OBJETIVOS = Object.entries(OBJETIVO_LABELS);
const EMPTY_ROUTINE = { nombre: "", nivel: 1, objetivo: "salud_general", dias_por_semana: 3, tipo: "estandar", ejercicios: [] };
const EMPTY_EX = { dia: 1, exercise_id: "", series: 3, repeticiones: "15" };

export default function RutinasTab() {
  const [routines, setRoutines] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [err, setErr] = useState("");
  // AI Generation state
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiState, setAiState] = useState(null);
  const [aiPolling, setAiPolling] = useState(false);
  // Delete all state
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, exs] = await Promise.all([adminApi.listRoutines(), ejerciciosApi.adminList()]);
      setRoutines(r); setExercises(exs.filter((x) => x.activa));
    } catch { setErr("Error cargando datos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Poll AI generation state
  const pollAiState = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/rutinas/generar-ia/estado`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      const d = await res.json();
      setAiState(d);
      if (d.estado === "en_progreso") {
        setTimeout(pollAiState, 2000);
      } else if (d.estado === "completado") {
        setAiPolling(false);
        await load();
      }
    } catch { /* ignore */ }
  }, []);

  const handleLaunchAi = async () => {
    setShowAiModal(false);
    setAiPolling(true);
    try {
      const res = await fetch(`${API}/api/admin/rutinas/generar-ia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      if (!res.ok) {
        const d = await res.json();
        alert(d.detail || "Error iniciando generación");
        setAiPolling(false);
        return;
      }
      pollAiState();
    } catch { setAiPolling(false); alert("Error de conexión"); }
  };

  const handlePublish = async (routineId) => {
    setPublishing(routineId);
    try {
      await fetch(`${API}/api/admin/rutinas/${routineId}/publicar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      await load();
    } catch { alert("Error publicando"); }
    finally { setPublishing(null); }
  };

  const handlePublishAll = async () => {
    if (!window.confirm("¿Publicar todas las rutinas IA en borrador? Serán visibles para los socios.")) return;
    try {
      await fetch(`${API}/api/admin/rutinas/publicar-todas-ia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      await load();
    } catch { alert("Error"); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Eliminar la rutina "${r.nombre}"? Solo es posible si ningún socio la tiene asignada.`)) return;
    setDeleting(r.id);
    try { await adminApi.deleteRoutine(r.id); await load(); }
    catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error eliminando"); }
    finally { setDeleting(null); }
  };

  const handleSaved = async () => { setEditing(null); await load(); };

  const handleDeleteAll = async () => {
    if (deleteConfirm !== "BORRAR") return;
    setDeletingAll(true);
    try {
      await adminApi.deleteAllRoutines();
      setShowDeleteAll(false);
      setDeleteConfirm("");
      await load();
    } catch { setErr("Error al borrar todas las rutinas"); }
    finally { setDeletingAll(false); }
  };

  const draftAiCount = routines.filter((r) => r.generado_por === "ia" && r.estado === "borrador").length;

  return (
    <div className="p-6 lg:p-8" data-testid="rutinas-tab">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-xl font-black tracking-wider">Rutinas</h1>
          <p className="text-xs text-white/40 mt-0.5">{routines.length} rutina(s) disponibles</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAiModal(true)} data-testid="generar-ia-btn"
            disabled={aiPolling}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#F2C230]/40 text-[#F2C230] font-black text-[11px] tracking-[0.2em] uppercase hover:bg-[#F2C230]/10 transition-colors disabled:opacity-40">
            {aiPolling ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {aiPolling ? "Generando..." : "Generar con IA"}
          </button>
          <button onClick={() => setEditing({ ...EMPTY_ROUTINE })} data-testid="nueva-rutina-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
            <Plus size={14} /> Nueva rutina
          </button>
          <button onClick={() => { setShowDeleteAll(true); setDeleteConfirm(""); }} data-testid="delete-all-routines-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-red-500/40 text-red-400 font-black text-[11px] tracking-[0.2em] uppercase hover:bg-red-500/10 transition-colors">
            <Trash2 size={14} /> Borrar todo
          </button>
        </div>
      </div>

      {/* AI Progress bar */}
      {(aiPolling || (aiState && aiState.estado === "completado")) && aiState && (
        <div className="mb-5 border border-white/10 bg-[#0a0a0a] p-4" data-testid="ai-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-black tracking-wider uppercase text-[#F2C230]">
              {aiState.estado === "en_progreso" ? "Generando rutinas con IA..." : "Generación completada"}
            </p>
            <span className="text-xs text-white/50">{aiState.completadas}/{aiState.total}</span>
          </div>
          <div className="w-full bg-white/8 h-1.5 overflow-hidden">
            <div
              className="h-full bg-[#F2C230] transition-all duration-500"
              style={{ width: `${Math.round((aiState.completadas / aiState.total) * 100)}%` }}
            />
          </div>
          {aiState.fallidas > 0 && (
            <div className="mt-2">
              <p className="text-xs text-red-400/80">{aiState.fallidas} combinaciones fallidas</p>
              {aiState.errores?.length > 0 && (
                <details className="mt-1">
                  <summary className="text-[10px] text-white/30 cursor-pointer hover:text-white/60">Ver detalle de errores</summary>
                  <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                    {aiState.errores.map((e, i) => (
                      <p key={i} className="text-[10px] text-red-300/70 font-mono">{e}</p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          {aiState.estado === "completado" && (
            <div className="flex items-center gap-2 mt-3">
              <CheckCircle size={13} className="text-green-400" />
              <span className="text-xs text-green-400">{aiState.completadas} rutinas generadas correctamente.</span>
              {draftAiCount > 0 && (
                <button onClick={handlePublishAll} data-testid="publish-all-ia-btn"
                  className="ml-auto text-[10px] font-black tracking-wider uppercase px-3 py-1.5 bg-[#F2C230] text-black hover:bg-white transition-colors">
                  Publicar todas ({draftAiCount})
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {err && <p className="text-red-400 text-sm mb-4">{err}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 size={18} className="animate-spin mr-2" /> Cargando...
        </div>
      ) : routines.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] py-16 text-center" data-testid="no-routines">
          <ClipboardList size={32} className="mx-auto text-white/15 mb-4" />
          <p className="text-white/35 text-sm">No hay rutinas todavía.</p>
          <button onClick={() => setEditing({ ...EMPTY_ROUTINE })} className="mt-4 text-[11px] font-bold text-[#FFCC00] tracking-wider uppercase hover:text-white transition-colors">
            Crear la primera rutina
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="routines-list">
          {routines.map((r) => {
            const isIaDraft = r.generado_por === "ia" && r.estado === "borrador";
            return (
              <div key={r.id} className={`border ${isIaDraft ? "border-[#F2C230]/20 bg-[#F2C230]/3" : "border-white/10 bg-[#0a0a0a]"} px-5 py-4 flex items-center justify-between gap-4`} data-testid={`routine-row-${r.id}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex flex-col gap-1.5 mt-0.5">
                    <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 ${
                      r.nivel === 1 ? "bg-green-500/15 text-green-400" : r.nivel === 2 ? "bg-yellow-500/15 text-yellow-400" : "bg-red-500/15 text-red-400"}`}>
                      {NIVEL_LABELS[r.nivel] || `N${r.nivel}`}
                    </span>
                    <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 ${
                      r.tipo === "personalizada" ? "bg-purple-500/15 text-purple-400 border border-purple-500/20" : "bg-white/6 text-white/40"}`}>
                      {TIPO_LABELS[r.tipo || "estandar"]}
                    </span>
                    {isIaDraft && (
                      <span className="text-[9px] font-black tracking-widest uppercase px-2 py-0.5 bg-[#F2C230]/15 text-[#F2C230] border border-[#F2C230]/30" data-testid={`ia-draft-badge-${r.id}`}>
                        IA · Borrador
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-white truncate">{r.nombre}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">
                      {OBJETIVO_LABELS[r.objetivo] || r.objetivo} · {r.dias_por_semana} días/semana · {r.ejercicios?.length || 0} ejercicios
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isIaDraft && (
                    <button onClick={() => handlePublish(r.id)} disabled={publishing === r.id} data-testid={`publish-routine-${r.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F2C230]/20 border border-[#F2C230]/40 text-[#F2C230] text-[10px] font-black tracking-wider uppercase hover:bg-[#F2C230]/30 transition-colors disabled:opacity-40">
                      {publishing === r.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                      Publicar
                    </button>
                  )}
                  <button onClick={() => setEditing({ ...r })} data-testid={`edit-routine-${r.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-white/45 hover:border-[#FFCC00]/60 hover:text-[#FFCC00] text-[10px] font-bold tracking-wider uppercase transition-colors">
                    <Pencil size={11} /> Editar
                  </button>
                  <button onClick={() => handleDelete(r)} disabled={deleting === r.id} data-testid={`delete-routine-${r.id}`}
                    className="p-2 border border-white/10 text-white/35 hover:border-red-500/50 hover:text-red-400 transition-colors disabled:opacity-40">
                    {deleting === r.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Generation Confirmation Modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ai-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowAiModal(false)} />
          <div className="relative w-full max-w-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F2C230]/10 border border-[#F2C230]/30 flex items-center justify-center">
                <Sparkles size={18} className="text-[#F2C230]" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black">Generador IA</p>
                <p className="text-sm font-bold text-white">Rutinas estándar en lote</p>
              </div>
            </div>
            <div className="space-y-3 mb-6 text-xs text-white/55 leading-relaxed">
              <p>Se generarán <strong className="text-white">45 rutinas</strong> cubriendo todas las combinaciones de:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>3 niveles: Iniciación, Intermedio, Avanzado</li>
                <li>3 duraciones: 3, 4 y 5 días/semana</li>
                <li>5 objetivos: Salud general, Ganar músculo, Perder grasa, Ganar fuerza, Resistencia</li>
              </ul>
              <p>Cada rutina se guardará como <strong className="text-[#F2C230]">IA · Borrador</strong>. Podrás revisar y publicar las que quieras.</p>
              <div className="border border-amber-500/25 bg-amber-500/8 px-3 py-2 text-amber-200/70">
                Si ya existen borradores IA para alguna combinación, se sobreescribirán. Las rutinas publicadas no se tocan.
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleLaunchAi} data-testid="confirm-ai-gen"
                className="flex-1 flex items-center justify-center gap-2 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.2em] uppercase py-3 hover:bg-white transition-colors">
                <Sparkles size={13} /> Generar 45 rutinas
              </button>
              <button onClick={() => setShowAiModal(false)}
                className="px-5 border border-white/12 text-white/50 text-[11px] font-bold tracking-wider uppercase hover:border-white/30 hover:text-white transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {editing !== null && (
        <RoutineModal
          routine={editing}
          machines={exercises}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {showDeleteAll && (
        <DeleteAllModal
          title="Borrar todas las rutinas"
          description={`Se eliminarán permanentemente las ${routines.length} rutinas y las asignaciones de socios. Esta acción no se puede deshacer.`}
          onConfirm={handleDeleteAll}
          onClose={() => setShowDeleteAll(false)}
          confirm={deleteConfirm}
          setConfirm={setDeleteConfirm}
          loading={deletingAll}
        />
      )}
    </div>
  );
}

function RoutineModal({ routine, machines: exercises, onClose, onSaved }) {
  const isNew = !routine.id;
  const [form, setForm] = useState({ ...routine });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addExercise = () => setForm((f) => ({
    ...f,
    ejercicios: [...f.ejercicios, { ...EMPTY_EX, dia: f.dias_por_semana > 0 ? 1 : 1 }],
  }));

  const updateEx = (i, k, v) => setForm((f) => ({
    ...f,
    ejercicios: f.ejercicios.map((e, idx) => idx === i ? { ...e, [k]: v } : e),
  }));

  const removeEx = (i) => setForm((f) => ({ ...f, ejercicios: f.ejercicios.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.nombre?.trim()) { setError("El nombre es obligatorio"); return; }
    setSaving(true); setError("");
    // Auto-assign orden per day
    const grouped = {};
    const enriched = form.ejercicios.map((e) => {
      const d = Number(e.dia);
      grouped[d] = (grouped[d] || 0) + 1;
      return { ...e, dia: d, orden: grouped[d], series: Number(e.series) };
    });
    const payload = { ...form, ejercicios: enriched };
    try {
      if (isNew) { await adminApi.createRoutine(payload); }
      else { await adminApi.updateRoutine(form.id, payload); }
      onSaved();
    } catch (e) { setError(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSaving(false); }
  };

  const INPUT = "w-full bg-[#111] border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="routine-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 shrink-0">
          <h2 className="text-sm font-black tracking-[0.18em] uppercase">{isNew ? "Nueva rutina" : "Editar rutina"}</h2>
          <button onClick={onClose} className="text-white/35 hover:text-white transition-colors"><X size={17} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Tipo */}
          <div>
            <label className="field-label">Tipo de rutina</label>
            <div className="flex border border-white/12">
              {[["estandar", "Estándar — visible para todos"], ["personalizada", "Personalizada — solo para el socio asignado"]].map(([val, lbl]) => (
                <button key={val} type="button" onClick={() => setF("tipo", val)} data-testid={`tipo-${val}`}
                  className={`flex-1 py-2.5 text-xs font-black tracking-wider uppercase transition-colors ${form.tipo === val ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {form.tipo === "personalizada" && (
              <p className="text-[10px] text-purple-400/70 mt-1.5">Esta rutina no aparecerá en el catálogo de socios. Solo la verá el socio al que se la asignes.</p>
            )}
          </div>

          {/* Nombre */}
          <div>
            <label className="field-label">Nombre *</label>
            <input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} placeholder="Ej: Iniciación Salud · 3 días" data-testid="routine-nombre" className={INPUT} />
          </div>

          {/* Nivel + Objetivo + Días */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="field-label">Nivel</label>
              <div className="flex gap-1">
                {[1, 2, 3].map((n) => (
                  <button key={n} type="button" onClick={() => setF("nivel", n)} data-testid={`nivel-${n}`}
                    className={`flex-1 py-2 text-xs font-black border transition-colors ${form.nivel === n ? "bg-[#FFCC00] border-[#FFCC00] text-black" : "border-white/12 text-white/50 hover:border-white/30"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="field-label">Objetivo</label>
              <select value={form.objetivo} onChange={(e) => setF("objetivo", e.target.value)} data-testid="routine-objetivo" className={INPUT + " appearance-none"}>
                {OBJETIVOS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Días / semana</label>
              <input type="number" min={1} max={7} value={form.dias_por_semana} onChange={(e) => setF("dias_por_semana", Number(e.target.value))} data-testid="routine-dias" className={INPUT} />
            </div>
          </div>

          {/* Exercises */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="field-label">Ejercicios ({form.ejercicios.length})</label>
              <button type="button" onClick={addExercise} data-testid="add-exercise-btn"
                className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-[#FFCC00] hover:text-white transition-colors">
                <Plus size={12} /> Añadir ejercicio
              </button>
            </div>

            {form.ejercicios.length === 0 ? (
              <div className="border border-white/8 border-dashed py-6 text-center text-white/25 text-sm">
                Sin ejercicios. Pulsa "Añadir ejercicio" para empezar.
              </div>
            ) : (
              <div className="border border-white/10 divide-y divide-white/5">
                <div className="grid grid-cols-[90px_1fr_80px_80px_36px] gap-2 px-3 py-2 bg-white/4">
                  {["Día", "Ejercicio", "Series", "Reps", ""].map((h) => (
                    <span key={h} className="text-[9px] font-black tracking-widest uppercase text-white/35">{h}</span>
                  ))}
                </div>
                {form.ejercicios.map((ex, i) => (
                  <div key={i} className="grid grid-cols-[90px_1fr_80px_80px_36px] gap-2 px-3 py-2 items-center" data-testid={`exercise-row-${i}`}>
                    <select value={ex.dia} onChange={(e) => updateEx(i, "dia", Number(e.target.value))} className="w-full bg-[#111] border border-white/10 text-white px-2 py-1.5 text-xs outline-none focus:border-[#FFCC00]">
                      {[1,2,3,4,5,6,7].map((d) => <option key={d} value={d}>Día {d}</option>)}
                    </select>
                    <select value={ex.exercise_id || ""} onChange={(e) => updateEx(i, "exercise_id", e.target.value)} data-testid={`ex-ejercicio-${i}`}
                      className="bg-[#111] border border-white/10 text-white px-2 py-1.5 text-xs outline-none focus:border-[#FFCC00]">
                      <option value="">— Ejercicio —</option>
                      {exercises.map((e) => {
                        const label = e.machine_nombre && e.machine_nombre !== e.nombre
                          ? `${e.codigo} · ${e.machine_nombre} — ${e.nombre}`
                          : `${e.codigo} · ${e.nombre}`;
                        return <option key={e.id} value={e.id}>{label}</option>;
                      })}
                    </select>
                    <input type="number" min={1} max={10} value={ex.series} onChange={(e) => updateEx(i, "series", e.target.value)} data-testid={`ex-series-${i}`}
                      className="bg-[#111] border border-white/10 text-white px-2 py-1.5 text-xs outline-none focus:border-[#FFCC00] text-center" />
                    <input type="text" value={ex.repeticiones} onChange={(e) => updateEx(i, "repeticiones", e.target.value)} placeholder="15" data-testid={`ex-reps-${i}`}
                      className="bg-[#111] border border-white/10 text-white px-2 py-1.5 text-xs outline-none focus:border-[#FFCC00] text-center" />
                    <button onClick={() => removeEx(i)} data-testid={`remove-ex-${i}`}
                      className="flex items-center justify-center text-white/25 hover:text-red-400 transition-colors w-8 h-8">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3" data-testid="routine-error">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-white/8 shrink-0">
          <button onClick={handleSave} disabled={saving} data-testid="save-routine-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase py-3.5 hover:bg-white transition-colors disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Guardando..." : (isNew ? "Crear rutina" : "Guardar cambios")}
          </button>
          <button onClick={onClose} className="px-6 border border-white/12 text-white/50 text-[11px] font-bold tracking-[0.2em] uppercase hover:border-white/35 hover:text-white transition-colors">
            Cancelar
          </button>
        </div>
      </div>
      <style>{`.field-label { display: block; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; font-weight: 700; color: rgba(255,255,255,0.45); margin-bottom: 6px; }`}</style>
    </div>
  );
}


function DeleteAllModal({ title, description, onConfirm, onClose, confirm, setConfirm, loading }) {
  const ready = confirm === "BORRAR";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-all-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#0d0d0d] border border-red-500/30 shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
            <Trash2 size={16} className="text-red-400" />
          </div>
          <h3 className="font-black text-sm tracking-wider text-white uppercase">{title}</h3>
        </div>
        <p className="text-white/50 text-xs leading-relaxed mb-5">{description}</p>
        <div className="mb-5">
          <label className="block text-[10px] uppercase tracking-widest text-white/35 font-bold mb-2">
            Escribe <span className="text-red-400 font-black">BORRAR</span> para confirmar
          </label>
          <input
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="BORRAR" autoFocus data-testid="delete-confirm-input"
            className="w-full bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-red-500 transition-colors tracking-widest font-bold"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={!ready || loading} data-testid="confirm-delete-all-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white font-black text-[10px] tracking-[0.2em] uppercase py-3 hover:bg-red-500 transition-colors disabled:opacity-30">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {loading ? "Borrando..." : "Confirmar borrado"}
          </button>
          <button onClick={onClose} className="px-4 border border-white/12 text-white/40 text-[10px] font-bold uppercase hover:text-white transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
